/**
 * Scope-based labels and graph styling for agent-memory UI.
 * Nodes are grouped by scope (station / device / shared) instead of brain-region anatomy.
 */

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export type ScopeLevel = 'station' | 'device' | 'shared';

export type MemoryGraphNodeType =
  | 'daily'
  | 'periodic'
  | 'topic'
  | 'station'
  | 'device'
  | 'knowledge_document'
  /** Central anchor node for a station or device (replaces the old 'signal' satellite). */
  | 'hub';

export type MemoryGraphLinkType =
  /** Connects nodes along the time axis: daily → periodic → topic. */
  | 'temporal'
  /** Connects nodes by ownership hierarchy: device → station, node → hub. */
  | 'ownership';

export type MemoryGraphFocus =
  | { kind: 'station'; stationId: string }
  | { kind: 'device'; deviceId: string };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SCOPE_LEVEL_ORDER: ScopeLevel[] = ['station', 'device', 'shared'];

export const SCOPE_LEVEL_COLORS: Record<ScopeLevel, string> = {
  station: '#0284c7',
  device:  '#7c3aed',
  shared:  '#64748b',
};

/** Scope levels that should get a visible hub anchor node. */
export const GRAPH_HUB_SCOPES: ScopeLevel[] = ['shared'];

export const LINK_COLORS: Record<MemoryGraphLinkType, string> = {
  temporal:  '#94a3b8',
  ownership: '#475569',
};

// ---------------------------------------------------------------------------
// Scope helpers
// ---------------------------------------------------------------------------

/** Primary scope for each node kind. */
export function scopeForNodeType(type: MemoryGraphNodeType): ScopeLevel {
  switch (type) {
    case 'station':
    case 'hub':
      return 'shared';
    case 'device':
    case 'daily':
      return 'device';
    case 'periodic':
    case 'topic':
    case 'knowledge_document':
      return 'station';
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

/** Scopes involved in each link kind (for link styling). */
export function scopesForLinkType(type: MemoryGraphLinkType): ScopeLevel[] {
  switch (type) {
    case 'temporal':
      return ['device', 'station'];
    case 'ownership':
      return ['shared', 'device'];
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

/** Secondary scopes that also touch this node kind (shown in detail panel). */
export function secondaryScopesForNodeType(type: MemoryGraphNodeType): ScopeLevel[] {
  switch (type) {
    case 'daily':
      return ['station'];
    case 'periodic':
      return ['device'];
    case 'topic':
      return ['device', 'shared'];
    case 'station':
      return ['device'];
    case 'device':
      return ['station'];
    case 'knowledge_document':
      return ['device'];
    case 'hub':
      return ['station', 'device'];
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

export function allScopesForNode(
  type: MemoryGraphNodeType,
  nodeScope?: ScopeLevel,
): ScopeLevel[] {
  if (type === 'hub' && nodeScope) {
    const rest = secondaryScopesForNodeType(type).filter((s) => s !== nodeScope);
    return [nodeScope, ...rest];
  }
  const primary = scopeForNodeType(type);
  const rest = secondaryScopesForNodeType(type).filter((s) => s !== primary);
  const scopes = [primary, ...rest];
  if (nodeScope && !scopes.includes(nodeScope)) {
    return [nodeScope, ...scopes];
  }
  return scopes;
}

// ---------------------------------------------------------------------------
// Highlight / involvement
// ---------------------------------------------------------------------------

export type ScopeInvolvement = 'primary' | 'secondary' | 'none';

const INVOLVEMENT_RANK: Record<ScopeInvolvement, number> = {
  none: 0,
  secondary: 1,
  primary: 2,
};

function maxInvolvement(a: ScopeInvolvement, b: ScopeInvolvement): ScopeInvolvement {
  return INVOLVEMENT_RANK[a] >= INVOLVEMENT_RANK[b] ? a : b;
}

export type GraphScopeHighlightInput = {
  id: string;
  type: MemoryGraphNodeType;
  scope: ScopeLevel;
};

export type GraphScopeLinkInput = {
  source: string;
  target: string;
  type: MemoryGraphLinkType;
};

/** How strongly a graph node relates to a scope level. */
export function scopeInvolvementForNode(
  nodeType: MemoryGraphNodeType,
  nodeScope: ScopeLevel,
  scope: ScopeLevel,
): ScopeInvolvement {
  if (nodeScope === scope) return 'primary';
  if (nodeType !== 'hub' && scopeForNodeType(nodeType) === scope) return 'primary';
  if (secondaryScopesForNodeType(nodeType).includes(scope)) return 'secondary';
  return 'none';
}

/** Per-node highlight tier when a scope filter is selected. */
export function computeGraphScopeHighlight(
  nodes: GraphScopeHighlightInput[],
  links: GraphScopeLinkInput[],
  scope: ScopeLevel | null,
): Map<string, ScopeInvolvement> {
  const map = new Map<string, ScopeInvolvement>();
  if (!scope) {
    nodes.forEach((n) => map.set(n.id, 'primary'));
    return map;
  }

  const setInv = (nodeId: string, level: ScopeInvolvement) => {
    map.set(nodeId, maxInvolvement(map.get(nodeId) ?? 'none', level));
  };

  nodes.forEach((n) => {
    const inv = scopeInvolvementForNode(n.type, n.scope, scope);
    if (inv !== 'none') setInv(n.id, inv);
  });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  links.forEach((l) => {
    if (!scopesForLinkType(l.type).includes(scope)) return;
    const src = nodeById.get(l.source);
    const tgt = nodeById.get(l.target);
    const srcInv = src ? scopeInvolvementForNode(src.type, src.scope, scope) : 'none';
    const tgtInv = tgt ? scopeInvolvementForNode(tgt.type, tgt.scope, scope) : 'none';
    if (srcInv === 'none') setInv(l.source, 'secondary');
    if (tgtInv === 'none') setInv(l.target, 'secondary');
  });

  nodes.forEach((n) => {
    if (!map.has(n.id)) map.set(n.id, 'none');
  });

  return map;
}

/**
 * Highlight map for filtering by a specific node id (station or device).
 * Nodes directly connected to the target are 'secondary'; all others 'none'.
 */
export function computeGraphNodeHighlight(
  nodes: GraphScopeHighlightInput[],
  links: GraphScopeLinkInput[],
  targetId: string | null,
): Map<string, ScopeInvolvement> {
  const map = new Map<string, ScopeInvolvement>();
  if (!targetId) {
    nodes.forEach((n) => map.set(n.id, 'primary'));
    return map;
  }

  const connected = new Set<string>();
  links.forEach((l) => {
    if (l.source === targetId) connected.add(l.target);
    if (l.target === targetId) connected.add(l.source);
  });

  nodes.forEach((n) => {
    if (n.id === targetId) map.set(n.id, 'primary');
    else if (connected.has(n.id)) map.set(n.id, 'secondary');
    else map.set(n.id, 'none');
  });

  return map;
}

export function linkInvolvementForScope(
  linkType: MemoryGraphLinkType,
  scope: ScopeLevel | null,
): ScopeInvolvement {
  if (!scope) return 'secondary';
  return scopesForLinkType(linkType).includes(scope) ? 'primary' : 'none';
}

// ---------------------------------------------------------------------------
// i18n helpers
// ---------------------------------------------------------------------------

export function i18nKeyForScope(id: ScopeLevel): string {
  return `memory.scopeLevel.${id}.name`;
}

export function i18nDescKeyForScope(id: ScopeLevel): string {
  return `memory.scopeLevel.${id}.role`;
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

export function summaryMatchesTopic(summaryMd: string, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return false;
  return summaryMd.toLowerCase().includes(kw);
}

export function isGraphNodeClickable(type: MemoryGraphNodeType): boolean {
  return (
    type === 'daily' ||
    type === 'periodic' ||
    type === 'station' ||
    type === 'device' ||
    type === 'hub' ||
    type === 'topic' ||
    type === 'knowledge_document'
  );
}

